import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { PasswordService } from '@app/finxone-web-frontend/app/lib/services/password-service/password-service.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { findAndShowAlertFromCode } from '@app/finxone-web-frontend/app/lib/utils/alert-code-utils/alert-code.utils';
import { passwordRulesValidator } from '@app/finxone-web-frontend/app/lib/utils/password-field.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { getNavigationUrl } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { ProfileErrorCodes } from '@app/translations';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  BaseCssProperties,
  NavigationOptionEnum,
  PasswordFieldConfigurations,
  ProviderClass,
  WidgetNames,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { debounceTime, map, takeUntil } from 'rxjs';
import {
  FormStage,
  MultiPartFormHolderService,
} from '../../../../../lib/services/multipart-holder/multipart-form-holder.service';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { defaultValidationMessages } from '../../../input-field-widget/default-validation-messages';
import { FormSubmitter } from '../../form-widgets/form-submitter/form-submitter.component';
import { PwnedPasswordService } from '../password-widget/pwned-password.service';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-password-forgot',
  templateUrl: './password-forgot.component.html',
  styleUrls: ['./password-forgot.component.scss'],
})
export class PasswordForgotComponent extends FormSubmitter implements OnInit {
  public buttonContent = '';
  public renderedButtonContent = '';
  public zone = '';
  public enableSubmit = false;
  public passwordForm: FormGroup;
  public reviewDetailsButtonStyle: baseCssConfigStyle = {};
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public mergedHeadingStyle: baseCssConfigStyle;
  public passwordFieldConfigs: PasswordFieldConfigurations = {} as any;
  public fieldTypes: { [key: string]: 'password' | 'text' } = {};
  public renderedContent = '';

  public isError = false;
  public errorMsg = '';
  public isPasswordPwned = false;
  private lastCheckedPassword = '';
  public passwordRulesPassed: boolean;
  public passwordRulesValidatorFn = passwordRulesValidator(() => this.passwordRulesPassed);
  public customValidationMessages: { [key: string]: string } | undefined;
  public validationMessages = defaultValidationMessages;

  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    private _router: Router,
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    private configService: ConfigService,
    public override store: Store,
    public formHolderService: MultiPartFormHolderService,
    private formSubmissionService: FormSubmissionService,
    private passwordService: PasswordService,
    private urlZoneInput: ZonePageUrlProvider,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private accountService: AccountService,
    private alertHandlerService: AlertHandlerService,
    private pwnedPasswordService: PwnedPasswordService,
  ) {
    super(store);
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.BORDER,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
    ]);

    this.zone = this.urlZoneInput.zoneUrlInput;
    this.fetchPasswordFieldConfig();
  }

  private fetchPasswordFieldConfig() {
    this.configService
      .getPasswordFieldConfigurations()
      .pipe(
        map((config) => {
          this.passwordFieldConfigs = config ?? ({} as PasswordFieldConfigurations);
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.customValidationMessages =
      this.dataIn.widgetConfig?.attributes.widgetProperties?.inputFieldConfig?.validationMessages;
    this.createFormStage();
    this.buildForm();
    this.onLoadWidgetContent();

    this.reviewDetailsButtonStyle = this.configService.getSpecificBaseCssStyle(
      this.getNestedWidgetConfig(this.dataIn, 'reviewDetailsButton'),
      buttonNestedWidgetConfigStyles,
    );
    this.getGlobalStyling();
  }

  private onLoadWidgetContent(): void {
    this.buttonContent =
      this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton']?.widgetProperties.textContent ?? 'Next';
    const template = Handlebars.compile(this.buttonContent);
    buildHandleBarModel(
      this.store,
      this.buttonContent,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  private updatePasswordValidity(): void {
    console.log('Updating password validity');
    const passwordControl = this.passwordForm.get('newPassword');
    const confirmPasswordControl = this.passwordForm.get('confirmPassword');

    if (!passwordControl || !confirmPasswordControl) {
      this.isError = false;
      return;
    }

    const formDirty = passwordControl.dirty && confirmPasswordControl.dirty;
    const passwordsMatch = this.passwordForm.hasError('noPasswordMatch');
    const anyFieldDirty = passwordControl.dirty || confirmPasswordControl.dirty;

    this.isError = anyFieldDirty && (passwordsMatch || this.isPasswordPwned);

    if (this.isPasswordPwned && anyFieldDirty) {
      this.errorMsg =
        this.customValidationMessages?.['passwordInBreach'] ||
        'This password has been found in a data breach and is not secure. Please choose a different password.';
    } else if (passwordsMatch && formDirty) {
      this.errorMsg = this.customValidationMessages?.['passwordMatch'] || 'Passwords do not match';
    } else {
      this.errorMsg = '';
    }
  }

  private pwnedPasswordFormValidator(): ValidationErrors | null {
    return this.isPasswordPwned ? { pwnedPassword: true } : null;
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton'].widgetProperties?.[
        'globalStylingClass'
      ] &&
      this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton'].widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton'].widgetProperties?.[
          'globalStylingClass'
        ] ?? '';
      this.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton'].widgetProperties?.['globalStyling'] ??
        false;
    }
  }

  private createFormStage(): void {
    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.PASSWORD_FORGOT,
      route: '',
      form: new FormGroup({
        newPassword: new FormControl('', Validators.required),
        confirmPassword: new FormControl('', Validators.required),
      }),
    });
    this.fieldTypes['newPassword'] = 'password';
    this.fieldTypes['confirmPassword'] = 'password';
    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);
  }

  private buildForm(): void {
    const stage = this.formHolderService.config.getStage(WidgetNames.PASSWORD_FORGOT);
    this.passwordForm = stage.getForm();

    this.passwordForm.addValidators([passwordMatchValidator, this.pwnedPasswordFormValidator.bind(this)]);
    this.passwordForm.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((values) => {
      const password = values?.newPassword || '';
      const confirmPassword = values?.confirmPassword || '';
      const passwordsMatch = password === confirmPassword && password.length >= 8;

      if (passwordsMatch && password !== this.lastCheckedPassword) {
        this.lastCheckedPassword = password;
        this.pwnedPasswordService
          .checkPassword(password)
          .pipe(takeUntil(this.destroy$))
          .subscribe((isPwned) => {
            this.isPasswordPwned = isPwned;
            this.passwordForm.updateValueAndValidity({ emitEvent: false });
            this.updatePasswordValidity();
          });
      } else if (!passwordsMatch) {
        this.isPasswordPwned = false;
        this.lastCheckedPassword = '';
        this.passwordForm.updateValueAndValidity({ emitEvent: false });
      }

      this.updatePasswordValidity();
    });
  }

  public onSubmit(): void {
    if (this.passwordForm.valid) {
      const formValues: Record<string, any> = this.passwordForm.value;

      this.formSubmissionService
        .submitIndividualForm(formValues, '', FormActionTypeEnum.PASWORD_CHANGE)
        .then((success) => {
          if (!success || !this.enableSubmit) {
            return;
          }
          this.store
            .select(FormActionState.getFormActionState)
            .pipe(
              takeUntil(this.destroy$),
              map((formActionStateData) => {
                return formActionStateData[FormActionTypeEnum.PASWORD_CHANGE]?.formData;
              }),
            )
            .subscribe({
              next: ({ currentPassword, newPassword, confirmPassword }) => {
                if (currentPassword === newPassword) {
                  return findAndShowAlertFromCode(this.store, this._router, this.alertHandlerService, [
                    ProfileErrorCodes.NEW_AND_OLD_PASSWORD_SAME,
                  ]);
                }

                this.executePasswordSubmission(currentPassword, newPassword, confirmPassword);

                this.enableSubmit = false;
              },
            });

          let name = '';
          Object.values(formValues).forEach((value: string) => {
            name = name + value + ' ';
          });
        });
    } else {
      this.passwordForm.markAllAsTouched();
    }
  }

  private executePasswordSubmission(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): void {
    if (this.dataIn.widgetConfig.name === WidgetNames.PASSWORD_UPDATE) {
      this.passwordService.resetPassword(currentPassword, newPassword, confirmPassword).subscribe({
        next: () => {
          const redirectUrl =
            getNavigationUrl(this.dataIn.attri) ||
            this.configService.reRouteIfModal(APP_ZONES.PROFILE, 'change-password-success', false);

          if (redirectUrl && redirectUrl === NavigationOptionEnum.USE_AS_BLANK) return;
          this._router.navigateByUrl(redirectUrl);
        },
        error: (err) => {
          console.error(err);
        },
      });
    } else {
      const splitUrl = window.location.href.split('/');
      const token = splitUrl[splitUrl.length - 1];
      this.passwordService.updatePassword(token, newPassword, confirmPassword).subscribe({
        next: () => {
          const redirectUrl =
            getNavigationUrl(this.dataIn.attri) ||
            this.configService.reRouteIfModal(APP_ZONES.SIGNED_OUT_FORGOT_PASSWORD, 'pass-reset-validation');

          if (redirectUrl && redirectUrl === NavigationOptionEnum.USE_AS_BLANK) return;
          this._router.navigateByUrl(redirectUrl);
        },
        error: (err) => {
          console.error(err);
        },
      });
    }
  }

  public togglePasswordFieldType(fieldName: string, value: 'password' | 'text') {
    this.fieldTypes[fieldName] = value;
  }

  public get formIsValid(): boolean {
    const formData = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(FormActionTypeEnum.PASWORD_CHANGE),
    )?.formData;
    const currentPasswordFieldIsValid =
      this.zone === formatNameAsUrl(APP_ZONES.SIGNED_OUT_FORGOT_PASSWORD)
        ? true
        : !!formData?.currentPassword?.length;
    return (currentPasswordFieldIsValid && this.passwordForm.valid) || false;
  }

  public allPasswordRulesPassed(passed: boolean): void {
    this.passwordRulesPassed = passed;
  }

  public getRequiredMessage(label: string, field: string): string {
    return this.customValidationMessages?.[field] || this.validationMessages.required(label);
  }
}

export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.get('newPassword')?.value;
  const confirmPassword = control.get('confirmPassword')?.value;

  if (!password || !confirmPassword) {
    return null; // don't validate if one is empty
  }

  return password === confirmPassword ? null : { noPasswordMatch: true };
};
